// ---------------------------------------------------------------------------
// Admin enrichment API client
// ---------------------------------------------------------------------------
// Not in the OpenAPI spec — plain fetch with credentials: "include" (cookie auth).

const API_BASE =
  typeof window === "undefined"
    ? process.env.ALISTER_API_URL ?? "http://127.0.0.1:8000"
    : "";

export interface AdminLinkSourceResponse {
  status: "linked" | "conflict" | "not_found" | "rate_limited" | "error";
  message: string;
  candidate_id: number;
  source_record_id: number | null;
  conflict_entity_id: number | null;
}

export interface AdminMergeEntitiesResponse {
  status: "merged" | "error";
  message: string;
  winner_entity_id: number;
  loser_entity_id: number;
  loser_deleted: boolean;
}

export interface AdminFetchGithubResponse {
  status: "found_from_db" | "fetched_live" | "not_found" | "rate_limited" | "generic_error";
  candidate_id: number | null;
  github_login: string | null;
  display_name: string | null;
  message: string | null;
}

export async function adminFetchGithub(
  username: string
): Promise<AdminFetchGithubResponse> {
  const url = `${API_BASE}/api/v1/profiles/admin/fetch-github?username=${encodeURIComponent(username)}`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 403) {
    throw new Error("Admin access required");
  }
  if (response.status === 429) {
    const body = await response.json();
    throw new Error(body.detail ?? "Rate limit exceeded");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

export interface AdminFetchLinkedinResponse {
  status: "found_from_db" | "fetched_live" | "not_found" | "rate_limited" | "generic_error";
  candidate_id: number | null;
  linkedin_handle: string | null;
  display_name: string | null;
  message: string | null;
}

export async function adminFetchLinkedin(
  identifier: string
): Promise<AdminFetchLinkedinResponse> {
  const url = `${API_BASE}/api/v1/profiles/admin/fetch-linkedin?identifier=${encodeURIComponent(identifier)}`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 403) {
    throw new Error("Admin access required");
  }
  if (response.status === 429) {
    const body = await response.json();
    throw new Error(body.detail ?? "Rate limit exceeded");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

export async function adminLinkGithub(
  candidateId: string,
  githubUsername: string
): Promise<AdminLinkSourceResponse> {
  const url = `${API_BASE}/api/v1/profiles/${encodeURIComponent(candidateId)}/admin/link-github`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identifier: githubUsername }),
  });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 403) {
    throw new Error("Admin access required");
  }
  if (response.status === 429) {
    const body = await response.json();
    throw new Error(body.detail ?? "Rate limit exceeded");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

export async function adminLinkLinkedin(
  candidateId: string,
  linkedinIdentifier: string
): Promise<AdminLinkSourceResponse> {
  const url = `${API_BASE}/api/v1/profiles/${encodeURIComponent(candidateId)}/admin/link-linkedin`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identifier: linkedinIdentifier }),
  });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 403) {
    throw new Error("Admin access required");
  }
  if (response.status === 429) {
    const body = await response.json();
    throw new Error(body.detail ?? "Rate limit exceeded");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

async function _adminRefreshSource(
  candidateId: string,
  source: "linkedin" | "github" | "summary"
): Promise<AdminLinkSourceResponse> {
  const url = `${API_BASE}/api/v1/profiles/${encodeURIComponent(candidateId)}/admin/refresh-${source}`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 403) {
    throw new Error("Admin access required");
  }
  if (response.status === 429) {
    const body = await response.json();
    throw new Error(body.detail ?? "Rate limit exceeded");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

export function adminRefreshLinkedin(
  candidateId: string
): Promise<AdminLinkSourceResponse> {
  return _adminRefreshSource(candidateId, "linkedin");
}

export function adminRefreshGithub(
  candidateId: string
): Promise<AdminLinkSourceResponse> {
  return _adminRefreshSource(candidateId, "github");
}

export function adminRefreshSummary(
  candidateId: string
): Promise<AdminLinkSourceResponse> {
  return _adminRefreshSource(candidateId, "summary");
}

// ---------------------------------------------------------------------------
// Thesis matching
// ---------------------------------------------------------------------------

export interface ThesisMatchEntry {
  match_id: number;
  thesis_id: number;
  title: string;
  thesis_type: string;
  university: string | null;
  year: number | null;
  advisor: string | null;
  url: string | null;
  confidence: string; // "verified" | "name_only"
  match_source: string; // "auto" | "admin_confirmed"
}

export interface ThesisMatchesResponse {
  matches: ThesisMatchEntry[];
  search_executed: boolean;
}

export async function fetchThesisMatches(
  candidateId: string
): Promise<ThesisMatchesResponse> {
  const url = `${API_BASE}/api/v1/profiles/${encodeURIComponent(candidateId)}/theses`;
  try {
    const response = await fetch(url, {
      credentials: "include",
    });

    if (response.status === 401 || response.status === 403) {
      return { matches: [], search_executed: false };
    }
    if (!response.ok) {
      return { matches: [], search_executed: false };
    }

    return response.json();
  } catch {
    return { matches: [], search_executed: false };
  }
}

export async function adminSearchTheses(
  candidateId: string
): Promise<ThesisMatchesResponse> {
  const url = `${API_BASE}/api/v1/profiles/${encodeURIComponent(candidateId)}/admin/search-theses`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 403) {
    throw new Error("Admin access required");
  }
  if (response.status === 429) {
    const body = await response.json();
    throw new Error(body.detail ?? "Rate limit exceeded");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

export async function adminSearchThesesLive(
  candidateId: string
): Promise<ThesisMatchesResponse> {
  const url = `${API_BASE}/api/v1/profiles/${encodeURIComponent(candidateId)}/admin/search-theses-live`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Live search failed: ${response.status}`);
  }

  return response.json();
}

export async function adminConfirmThesisMatch(
  candidateId: string,
  matchId: number
): Promise<ThesisMatchEntry> {
  const url = `${API_BASE}/api/v1/profiles/${encodeURIComponent(candidateId)}/admin/thesis-match/${matchId}/confirm`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 403) {
    throw new Error("Admin access required");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

export async function adminRejectThesisMatch(
  candidateId: string,
  matchId: number
): Promise<{ status: string }> {
  const url = `${API_BASE}/api/v1/profiles/${encodeURIComponent(candidateId)}/admin/thesis-match/${matchId}/reject`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 403) {
    throw new Error("Admin access required");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Keyword profile
// ---------------------------------------------------------------------------

export interface KeywordProfileResponse {
  keyword_profile: Record<string, unknown> | null;
  generated_at: string | null;
  prompt_version: number | null;
}

export interface AdminGenerateKeywordProfileResponse {
  status: "generated" | "skipped" | "error";
  message: string;
  keyword_profile: Record<string, unknown> | null;
  generated_at: string | null;
  prompt_version: number | null;
}

export async function getKeywordProfile(
  candidateId: string
): Promise<KeywordProfileResponse> {
  const url = `${API_BASE}/api/v1/profiles/${encodeURIComponent(candidateId)}/keyword-profile`;
  try {
    const response = await fetch(url, { credentials: "include" });
    if (!response.ok) {
      return { keyword_profile: null, generated_at: null, prompt_version: null };
    }
    return response.json();
  } catch {
    return { keyword_profile: null, generated_at: null, prompt_version: null };
  }
}

export async function adminGenerateKeywordProfile(
  candidateId: string
): Promise<AdminGenerateKeywordProfileResponse> {
  const url = `${API_BASE}/api/v1/profiles/${encodeURIComponent(candidateId)}/admin/generate-keyword-profile`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 403) {
    throw new Error("Admin access required");
  }
  if (response.status === 429) {
    const body = await response.json();
    throw new Error(body.detail ?? "Rate limit exceeded");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

export async function adminMergeEntities(
  winnerEntityId: number,
  loserEntityId: number
): Promise<AdminMergeEntitiesResponse> {
  const url = `${API_BASE}/api/v1/profiles/admin/merge-entities`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      winner_entity_id: winnerEntityId,
      loser_entity_id: loserEntityId,
    }),
  });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 403) {
    throw new Error("Admin access required");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}
